import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Space, Tag, Tooltip } from 'antd';
import { CalendarOutlined, CopyOutlined, FileExcelOutlined, PlusOutlined } from '@ant-design/icons';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { API } from '@/services/api.service';
import BatchCalculationModal from './BatchCalculationModal';
import CalculationDrawer from './CalculationDrawer';
import ColumnTitle from './ColumnTitle';
import PaymentSlipModal from './PaymentSlipModal';
import TabTable from './TabTable';
import { CALC_KIND_COLORS, STATUS_COLORS, errorText, money, saveBlob } from './format';
import { paramsOf, withoutPaging } from './listParams';

/**
 * Rent and other income calculated on the platform, a page at a time from
 * the server, with the return each one was filed as and whether it is paid.
 * A new period of the same flat starts from its last calculation („copy for
 * the next period").
 */
export default function CalculationsPanel({ company, projects, canManage, onOpenReturn }) {
  const { t } = useTranslation('taxReturns');
  const queryClient = useQueryClient();
  const { queryParams } = useQueryParamsContext();
  const params = useMemo(() => paramsOf('calculations', queryParams), [queryParams]);
  const [drawer, setDrawer] = useState(null); // { id } | { template } | {}
  const [slip, setSlip] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax-calculations', company, params],
    queryFn: () => API.taxReturns.getCalculations({ company, ...params }),
    enabled: Boolean(company),
    placeholderData: (prev) => prev,
  });
  const items = data?.items || [];

  // Accounts the orders can be paid from: those seen on the company's statements.
  const { data: accounts = [] } = useQuery({
    queryKey: ['tax-returns-accounts', company],
    queryFn: () => API.taxReturns.getBankAccounts(company),
    enabled: Boolean(company && canManage),
  });
  const payerAccounts = accounts.map((a) => ({
    value: a.accountFormatted,
    label: `${a.accountFormatted}${a.project ? ` · ${a.project.code} ${a.project.name}` : ''}`,
  }));

  // The newest calculation of every flat, for the whole company, to start the next one from.
  const latestBySubject = useMemo(() => Object.fromEntries((data?.latest || []).map((c) => [c.subject, c])), [data]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tax-calculations', company] });
    queryClient.invalidateQueries({ queryKey: ['tax-properties', company] });
  };

  const exportMutation = useMutation({
    mutationFn: () => {
      const filters = withoutPaging(params);
      return API.taxReturns.exportCalculationOrders(company, filters, {
        sheet: t('calc.export.sheet'),
        project: t('fields.project'),
        subject: t('calc.fields.subject'),
        payer: t('calc.export.payer'),
        payerAccount: t('calc.fields.payerAccount'),
        purpose: t('calc.fields.purposeText'),
        paymentCode: t('slip.paymentCode'),
        amount: t('fields.amount'),
        payee: t('slip.payee'),
        payeeAccount: t('fields.paymentAccount'),
        model: t('slip.model'),
        reference: t('fields.bop'),
        returnNumber: t('calc.fields.returnNumber'),
      });
    },
    onSuccess: (blob) => saveBlob(blob, `${t('calc.export.fileName')}.xlsx`),
  });

  const statusLabel = (s) => t(`status.${s}`, { context: canManage ? undefined : 'client' });

  const columns = [
    { title: () => <ColumnTitle title={t('fields.purpose')} sort="period" />, dataIndex: 'pid', width: 130 },
    {
      title: () => <ColumnTitle title={t('fields.project')} sort="project" />,
      dataIndex: 'project',
      width: 170,
      render: (p) => (p ? `${p.code} ${p.name}` : '—'),
    },
    {
      title: () => <ColumnTitle title={t('calc.fields.subject')} sort="subject" search />,
      dataIndex: 'subject',
      render: (v, c) => (
        <div>
          <div>{v || '—'}</div>
          <div className="text-xs opacity-60">
            <Tag color={CALC_KIND_COLORS[c.kind]}>{t(`calc.kinds.${c.kind}`)}</Tag>
            {c.coveredPeriod && <Tag>{c.coveredPeriod}</Tag>}
            {c.recipients.map((r) => r.name).join(', ')}
          </div>
        </div>
      ),
    },
    { title: t('calc.fields.months'), dataIndex: 'months', width: 80, align: 'right' },
    {
      title: () => <ColumnTitle title={t('calc.fields.netRsd')} sort="netRsd" />,
      dataIndex: ['totals', 'netRsd'],
      width: 150,
      align: 'right',
      render: money,
    },
    {
      title: () => <ColumnTitle title={t('calc.fields.taxShort')} sort="tax" />,
      dataIndex: ['totals', 'tax'],
      width: 140,
      align: 'right',
      render: (v, c) => (
        <div>
          <b>{money(v)}</b>
          {/* Supplementary work: the PIO contribution is paid with the tax. */}
          {c.totals?.pio > 0 && (
            <div className="text-xs opacity-60">
              {t('calc.fields.pioShort')} {money(c.totals.pio)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('fields.status'),
      width: 190,
      render: (c) => (
        <Space size={4} wrap>
          {c.incomplete ? (
            <Tag color="orange">{t('calc.incomplete')}</Tag>
          ) : (
            <Tag color={c.status === 'done' ? 'green' : 'default'}>{t(`calc.status.${c.status}`)}</Tag>
          )}
          {c.taxReturn && <Tag color={STATUS_COLORS[c.taxReturn.status]}>{statusLabel(c.taxReturn.status)}</Tag>}
        </Space>
      ),
    },
    canManage && {
      title: '',
      width: 60,
      align: 'center',
      render: (c) => (
        <Tooltip title={t('calc.copyNext')}>
          <Button
            shape="round"
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setDrawer({ template: c });
            }}
          />
        </Tooltip>
      ),
    },
  ].filter(Boolean);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <p className="text-sm opacity-70">{t(canManage ? 'calc.help.panel' : 'calc.help.panelClient')}</p>
      {(isError || exportMutation.isError) && <Alert type="error" showIcon message={errorText(t, error || exportMutation.error)} />}

      <TabTable
        tab="calculations"
        title={t('tabs.calculations')}
        data={items}
        total={data?.count || 0}
        status={isLoading ? 'pending' : isError ? 'error' : 'success'}
        columns={columns}
        projects={projects}
        actions={
          <>
            {canManage && (
              <>
                <Button shape="round" className="h-8" icon={<CalendarOutlined />} onClick={() => setBatchOpen(true)}>
                  {t('batch.button')}
                </Button>
                <Button shape="round" className="h-8" icon={<PlusOutlined />} onClick={() => setDrawer({})}>
                  {t('calc.new')}
                </Button>
              </>
            )}
            <Tooltip title={t('calc.export.hint')}>
              <Button
                shape="round"
                className="h-8"
                icon={<FileExcelOutlined />}
                loading={exportMutation.isPending}
                disabled={!data?.count}
                onClick={() => exportMutation.mutate()}
              >
                {t('calc.export.button')}
              </Button>
            </Tooltip>
          </>
        }
        onRow={(c) => ({ onClick: () => setDrawer({ id: c._id }), style: { cursor: 'pointer' } })}
      />

      <CalculationDrawer
        open={Boolean(drawer)}
        company={company}
        id={drawer?.id || null}
        template={drawer?.template || null}
        projects={projects}
        payerAccounts={payerAccounts}
        subjects={data?.subjects || []}
        latestBySubject={latestBySubject}
        canManage={canManage}
        onClose={() => setDrawer(null)}
        onSaved={refresh}
        onSlip={setSlip}
        onOpenReturn={onOpenReturn}
      />
      {canManage && (
        <BatchCalculationModal
          open={batchOpen}
          company={company}
          projects={projects}
          payerAccounts={payerAccounts}
          onClose={() => setBatchOpen(false)}
          onCreated={refresh}
        />
      )}
      <PaymentSlipModal slip={slip?.slip} title={slip?.title} note={t('calc.slipNote')} onClose={() => setSlip(null)} />
    </Space>
  );
}
